import React, { useState, useEffect } from "react";
import get from "lodash/get";
import { getFieldPath } from "utils/form";
import Image from "components/Image";
import {
  SERVICE_ACTION_LABELS,
  WORKFLOW_SERVICE_ACTION_OPTIONS,
  SERVICE_LABEL_MAP
} from "utils/constants/workflow";
import SlimCTA from "components/SlimCTA";
import MultiselectOption from "components/MultiselectOption";
import Recipients from "../../Recipients";
import MultiSelect from "components/Form/fields/MultiSelect";
import HoverIconButton from "components/HoverIconButton";
import { useFormikContext } from "formik";
import { FORM_KEY } from "utils/constants/form";
import {
  fetchDocusignTemplate,
  fetchDocusignTemplates,
  mergeDocusignTemplateRecipients
} from "utils/workflow/docusign";
import classnames from "classnames";
import { STATE_KEYS } from "utils/constants/state";
import { useDispatch } from "react-redux";
import { apiResourcePath } from "utils/paths";
import { useCtxReferences, useIntegration } from "utils/selectors";
import ActionRowLoader from "components/ActionRowLoader";
import ExternalLink from "components/ExternalLink";
import {
  DOCUSIGN_TEMPLATES_URL,
  getDocusignTemplateEditorUrl
} from "utils/constants/integration/docusign";
import FieldError from "components/FieldError";

const ACTION_SIZE = 14;
const DocusignStepEditor = ({ uuid, index, name }) => {
  const { resource, resourceUUID } = useCtxReferences();
  const {
    values,
    errors,
    initialValues,
    setFieldValue,
    setFieldTouched,
    touched
  } = useFormikContext();

  const dispatch = useDispatch();

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [fetchTemplatesError, setFetchTemplatesError] = useState(false);

  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [fetchTemplateError, setFetchTemplateError] = useState(false);

  const [switchingTemplate, setSwitchingTemplate] = useState(false);
  const steps = get(values, STATE_KEYS.WORKFLOW.STEPS) || [];
  const step = steps.find((step) => step.uuid === uuid);
  const isFocused = step && values[FORM_KEY].focusStepUuid === step.uuid;
  const service = step ? step.service : null;
  const templateId = get(step, "config.templateId");

  const initialSteps = get(initialValues, STATE_KEYS.WORKFLOW.STEPS) || [];
  const initialStep = initialSteps.find(
    (initialStep) => initialStep.uuid === uuid
  );

  const serviceLabel = SERVICE_LABEL_MAP[service];

  const recipientsTouched = get(touched, recipientsPath);
  const recipientsPath = getFieldPath(name, "config.recipients");
  const currentRecipients = get(values, recipientsPath) || {};
  const initialRecipients = get(initialValues, recipientsPath) || {};

  const actionPath = getFieldPath(name, "action");
  const actionTouched = get(touched, actionPath);
  const actionError = get(errors, actionPath);
  const showActionError = Boolean(actionTouched && actionError);

  const actionLabel = SERVICE_ACTION_LABELS[service][step.action];
  const activeActionOption = { id: step.action, name: actionLabel };

  const templateIdPath = getFieldPath(name, "config.templateId");
  const templateIdTouched = get(touched, templateIdPath);
  const templateIdError = get(errors, templateIdPath);
  const showTemplateIdError = Boolean(templateIdTouched && templateIdError);

  const integration = useIntegration(step.integrationId, "id");
  const integrationUuid = integration.uuid;

  const templates = get(integration, "_data.templates") || {};

  const templateOptions = [];
  for (const templateId in templates) {
    const template = templates[templateId];
    templateOptions.push({
      id: template.templateId,
      label: template.name,
      uid: template.templateId,
      description: template.description
    });
  }

  let activeTemplate;
  let activeTemplateOption;
  if (templateId) {
    activeTemplate = templates[templateId];

    if (activeTemplate) {
      activeTemplateOption = {
        id: activeTemplate.templateId,
        name: activeTemplate.name
      };
    }
  }

  const templatesUrl = `${apiResourcePath({
    resource,
    resourceUUID
  })}/integrations/${integrationUuid}/${service}/templates`;

  const fetchTemplate = async () => {
    await fetchDocusignTemplate({
      setLoading: setLoadingTemplate,
      url: `${templatesUrl}/${step.config.templateId}`,
      dispatch,
      integration,
      onError: (err) => setFetchTemplateError(err),
      onSuccess: (data) => {
        setFieldValue(
          recipientsPath,
          mergeDocusignTemplateRecipients({
            template: data,
            currentRecipients,
            initialRecipients
          })
        );
        if (!recipientsTouched) {
          setFieldTouched(recipientsPath, true);
        }
      }
    });
  };

  const fetchTemplates = async () => {
    await fetchDocusignTemplates({
      url: templatesUrl,
      setLoading: setLoadingTemplates,
      dispatch,
      integration,
      onError: (err) => setFetchTemplatesError(err)
    });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const needTemplateData =
    activeTemplate && (!activeTemplate.recipients || !activeTemplate.tabs);
  const shouldFetchTemplate = Boolean(
    templateId && needTemplateData && !fetchTemplateError && !loadingTemplate
  );

  useEffect(() => {
    if (shouldFetchTemplate) {
      fetchTemplate();
    }
  }, [shouldFetchTemplate]);

  useEffect(() => {
    if (activeTemplate && !needTemplateData) {
      setSwitchingTemplate(true);
      setFieldValue(
        recipientsPath,
        mergeDocusignTemplateRecipients({
          template: activeTemplate,
          currentRecipients,
          initialRecipients
        })
      );
      setTimeout(() => {
        setSwitchingTemplate(false);
      }, 500);
    } else if (!activeTemplate) {
      setFieldValue(recipientsPath, null);
    }
  }, [templateId]);

  const containerProps = {
    className: classnames("flex flex-column ba br2 hairline-1 w-100 bg-white", {
      "bg-white--hover pointer": !isFocused
    })
  };

  const actionProps = {
    value: activeActionOption ? [activeActionOption] : [],
    placeholder: `Select ${serviceLabel} action`,
    isMulti: false,
    options: WORKFLOW_SERVICE_ACTION_OPTIONS[service],
    onChange: () => ({ value }) => {
      if (!actionTouched) {
        setFieldTouched(actionPath, true);
      }

      setTimeout(() => setFieldValue(actionPath, value), 0);
    },
    labelTransform: (value) => value,
    components: {
      Option: MultiselectOption
    }
  };

  const anyTemplateLoading = Boolean(loadingTemplates || loadingTemplate);

  const templateProps = {
    value: activeTemplateOption ? [activeTemplateOption] : [],
    placeholder: `Select ${serviceLabel} template`,
    disabled: anyTemplateLoading,
    isMulti: false,
    options: [
      {
        id: "",
        label: "None",
        uid: "",
        description: ""
      },
      ...templateOptions
    ],
    onChange: () => ({ value }) => {
      if (!templateIdTouched) {
        setFieldTouched(templateIdPath, true);
      }

      setTimeout(() => setFieldValue(templateIdPath, value), 0);
    },
    labelTransform: (value) => value,
    components: {
      Option: MultiselectOption
    }
  };

  let content;
  if (!isFocused) {
    containerProps.onClick = () =>
      setFieldValue(`${FORM_KEY}.focusStepUuid`, uuid);
    content = (
      <div className="pa3 w-100 flex flex-row items-center o-50">
        <Image
          src={`/assets/integrations/${service}/icon.svg`}
          width={40}
          height={40}
          alt={service}
        ></Image>
        <div className="ph2 flex flex-row">
          <div className="f4 fw6 pr2">{index}</div>
          <div className="f4 fw5">{serviceLabel}</div>
        </div>
      </div>
    );
  } else {
    let templateContent;

    if (activeTemplate) {
      const fullTemplate = activeTemplate.recipients;
      if (loadingTemplate) {
        templateContent = <ActionRowLoader />;
      } else if (fullTemplate) {
        templateContent = <Recipients name={recipientsPath} />;
      } else {
        templateContent = <SlimCTA header="Fetch full template" />;
      }
    } else if (fetchTemplateError) {
      templateContent = <SlimCTA header="There was an error" />;
    } else if (!anyTemplateLoading) {
      templateContent = <SlimCTA header="Select a template first" />;
    }
    content = (
      <div className="w-100">
        <div className="pa3 w-100 flex flex-row items-center bb hairline-1">
          <Image
            src={`/assets/integrations/${service}/icon.svg`}
            width={40}
            height={40}
            alt={service}
          ></Image>
          <div className="ph2 flex flex-row">
            <div className="f4 fw6 pr2">{index + 1}</div>
            <div className="f4 fw5">{SERVICE_LABEL_MAP[service]}</div>
          </div>
        </div>
        <div className="w-100 pa3">
          <div className="flex flex-column">
            <label className="f6 fw6 db pb2">Action</label>
            <MultiSelect {...actionProps} />
            {showActionError && (
              <FieldError error={actionError} name={actionPath} />
            )}
          </div>
          <div className="flex flex-column pt3">
            <div className="flex flex-row justify-between items-center">
              <label className="f6 fw6 db pb2">Template</label>
              <div className="flex flex-row items-center">
                <div className="pr2 mr2 br hairline-1">
                  <ExternalLink
                    copy=" "
                    href={
                      templateId
                        ? getDocusignTemplateEditorUrl(templateId)
                        : DOCUSIGN_TEMPLATES_URL
                    }
                  />
                </div>
                <HoverIconButton
                  size={ACTION_SIZE}
                  onClick={fetchTemplates}
                  loading={loadingTemplates}
                  type="refresh"
                />
              </div>
            </div>
            <MultiSelect {...templateProps} />
            {showTemplateIdError && (
              <FieldError error={templateIdError} name={templateIdPath} />
            )}
          </div>
          <div className="flex flex-column pt3">
            {switchingTemplate ? <ActionRowLoader /> : templateContent}
          </div>
        </div>
      </div>
    );
  }

  return <div {...containerProps}>{content}</div>;
};

export default DocusignStepEditor;
